import { useState, useEffect } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Alert,
  FormControlLabel, Checkbox, Typography,
} from "@mui/material";

/**
 * A patient logs something extra they did ("Ran an extra mile") or plan to do.
 * Created already-done by default, since extras are mostly reported after the fact.
 * The care team can see what's added, but it's tracked separately and does NOT change
 * the patient's adherence score — the dialog says so up front.
 */
export default function AddActivityDialog({ open, saving, error, onClose, onSave }) {
  const [title, setTitle] = useState("");
  const [details, setDetails] = useState("");
  const [done, setDone] = useState(true);
  const [dueDate, setDueDate] = useState("");
  const [localError, setLocalError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setTitle("");
    setDetails("");
    setDone(true);
    setDueDate("");
    setLocalError(null);
  }, [open]);

  const handleSave = () => {
    if (!title.trim()) {
      setLocalError("Please say what you did.");
      return;
    }
    setLocalError(null);
    onSave({
      title: title.trim(),
      description: details.trim() || null,
      assigneeType: "PATIENT",
      alreadyDone: done,
      dueDate: done ? null : dueDate || null,
    });
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add an activity</DialogTitle>
      <DialogContent dividers>
        {(localError || error) && <Alert severity="error" sx={{ mb: 2 }}>{localError || error}</Alert>}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Log something extra you did, or something you plan to do. Your care team can see it. It's
          tracked as an extra and doesn't change your adherence score.
        </Typography>
        <TextField
          label="What did you do?" placeholder="e.g. Ran an extra mile" required fullWidth autoFocus
          value={title} inputProps={{ maxLength: 300 }} sx={{ mb: 2 }}
          onChange={(e) => setTitle(e.target.value)}
        />
        <TextField
          label="Details (optional)" multiline minRows={2} fullWidth value={details} sx={{ mb: 1 }}
          onChange={(e) => setDetails(e.target.value)}
        />
        <FormControlLabel
          control={<Checkbox checked={done} onChange={(e) => setDone(e.target.checked)} />}
          label="I already did this"
        />
        {!done && (
          <TextField
            label="Do it by (optional)" type="date" fullWidth value={dueDate} sx={{ mt: 1 }}
            InputLabelProps={{ shrink: true }} onChange={(e) => setDueDate(e.target.value)}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? "Saving…" : done ? "Log it" : "Add to my to-do list"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}